import { createContext, useContext, ReactNode, useState, useEffect, useCallback } from "react";
import { Position } from "./PositionCard";

// Type for invited user (matches Position.invited_users item)
type InvitedUser = NonNullable<Position["invited_users"]>[number];

type DressCodeType = { id: string; name: string; description?: string };
type LocationType = {
  id: string;
  name: string;
  address?: { street?: string; city?: string; state?: string; zip?: string };
  market_id?: string;
};
type AbilityType = { id: string; name: string };
type RestrictionType = { id: string; name: string };

interface PositionsContextValue {
  positions: Position[];
  loading: boolean;
  shiftId?: string;
  // Shift and project level data for inheritance
  shiftLocation?: LocationType;
  projectLocation?: LocationType;
  shiftDressCode?: DressCodeType;
  projectDressCode?: DressCodeType;
  // Markets for filtering
  shiftMarkets?: string[];
  projectMarkets?: string[];
  // Abilities (inherited chain: client -> project -> shift)
  clientAbilities?: AbilityType[];
  projectAbilities?: AbilityType[];
  shiftAbilities?: AbilityType[];
  // Restrictions (inherited chain: client -> project -> shift)
  clientRestrictions?: RestrictionType[];
  projectRestrictions?: RestrictionType[];
  shiftRestrictions?: RestrictionType[];
  addPosition: (position: Position) => void;
  addUserToPosition: (shiftPositionId: string, user: InvitedUser) => void;
  removePosition: (shiftPositionId: string) => void;
  removeUserFromPosition: (shiftPositionId: string, userId: string) => void;
  updatePosition: (shiftPositionId: string, updates: Partial<Position>) => void;
  updateUserInPosition: (shiftPositionId: string, userId: string, updates: Partial<InvitedUser>) => void;
}

const PositionsContext = createContext<PositionsContextValue | null>(null);

export function PositionsProvider({
  children,
  positions: initialPositions,
  loading = false,
  shiftId,
  shiftLocation,
  projectLocation,
  shiftDressCode,
  projectDressCode,
  shiftMarkets,
  projectMarkets,
  clientAbilities,
  projectAbilities,
  shiftAbilities,
  clientRestrictions,
  projectRestrictions,
  shiftRestrictions,
}: {
  children: ReactNode;
  positions: Position[];
  loading?: boolean;
  shiftId?: string;
  shiftLocation?: LocationType;
  projectLocation?: LocationType;
  shiftDressCode?: DressCodeType;
  projectDressCode?: DressCodeType;
  shiftMarkets?: string[];
  projectMarkets?: string[];
  clientAbilities?: AbilityType[];
  projectAbilities?: AbilityType[];
  shiftAbilities?: AbilityType[];
  clientRestrictions?: RestrictionType[];
  projectRestrictions?: RestrictionType[];
  shiftRestrictions?: RestrictionType[];
}) {
  const [positions, setPositions] = useState<Position[]>(initialPositions);

  // Sync state when prop changes (e.g., shift data refreshed)
  useEffect(() => {
    setPositions(initialPositions);
  }, [initialPositions]);

  const addPosition = useCallback((position: Position) => {
    setPositions((prev) => [position, ...prev]);
  }, []);

  const addUserToPosition = useCallback((shiftPositionId: string, user: InvitedUser) => {
    setPositions((prev) =>
      prev.map((pos) => {
        if (pos.shift_position_id !== shiftPositionId) return pos;
        return {
          ...pos,
          invited_users: [...(pos.invited_users || []), user],
        };
      }),
    );
  }, []);

  const removePosition = useCallback((shiftPositionId: string) => {
    setPositions((prev) => prev.filter((pos) => pos.shift_position_id !== shiftPositionId));
  }, []);

  const removeUserFromPosition = useCallback((shiftPositionId: string, userId: string) => {
    setPositions((prev) =>
      prev.map((pos) => {
        if (pos.shift_position_id !== shiftPositionId) return pos;
        return {
          ...pos,
          invited_users: (pos.invited_users || []).filter((u) => u.id !== userId),
        };
      }),
    );
  }, []);

  const updatePosition = useCallback((shiftPositionId: string, updates: Partial<Position>) => {
    setPositions((prev) =>
      prev.map((pos) => {
        if (pos.shift_position_id !== shiftPositionId) return pos;
        return { ...pos, ...updates };
      }),
    );
  }, []);

  const updateUserInPosition = useCallback((shiftPositionId: string, userId: string, updates: Partial<InvitedUser>) => {
    setPositions((prev) =>
      prev.map((pos) => {
        if (pos.shift_position_id !== shiftPositionId) return pos;
        return {
          ...pos,
          invited_users: (pos.invited_users || []).map((u) => (u.id === userId ? { ...u, ...updates } : u)),
        };
      }),
    );
  }, []);

  return (
    <PositionsContext.Provider
      value={{
        positions,
        loading,
        shiftId,
        shiftLocation,
        projectLocation,
        shiftDressCode,
        projectDressCode,
        shiftMarkets,
        projectMarkets,
        clientAbilities,
        projectAbilities,
        shiftAbilities,
        clientRestrictions,
        projectRestrictions,
        shiftRestrictions,
        addPosition,
        addUserToPosition,
        removePosition,
        removeUserFromPosition,
        updatePosition,
        updateUserInPosition,
      }}
    >
      {children}
    </PositionsContext.Provider>
  );
}

export function usePositions() {
  const context = useContext(PositionsContext);
  if (!context) {
    throw new Error("usePositions must be used within a PositionsProvider");
  }
  return context;
}
